import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { richTooltips } from '@dha-nexus/shared';
import { RichTooltipCard } from './RichTooltipCard.jsx';

const { RICH_TOOLTIP_INTERACTION_MODES } = richTooltips;

const RichTooltipContext = createContext(null);
const CLOSE_DELAY_MS = 120;
// Upper-bound visibility timer. After this many ms a tooltip auto-fades even
// if pinned. The timer pauses while the cursor is over the tooltip body and
// resumes when the cursor leaves. Re-hover on the trigger resets to zero.
const AUTO_FADE_MS = 5000;

function rectFromNode(node) {
  if (!node?.getBoundingClientRect) return null;
  const rect = node.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

export function RichTooltipProvider({ children, disabled = false }) {
  const location = useLocation();
  const stateRef = useRef(null);
  const closeTimerRef = useRef(null);
  const autoFadeTimerRef = useRef(null);
  const autoFadeStartRef = useRef(0);
  const autoFadeElapsedRef = useRef(0);
  const linkRef = useRef(null);
  const tooltipRef = useRef(null);
  const triggerRefs = useRef(new Map());
  const [state, setState] = useState(null);

  stateRef.current = state;

  const clearCloseTimer = useCallback(() => {
    if (!closeTimerRef.current) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const clearAutoFade = useCallback(() => {
    if (autoFadeTimerRef.current) {
      window.clearTimeout(autoFadeTimerRef.current);
      autoFadeTimerRef.current = null;
    }
    autoFadeStartRef.current = 0;
    autoFadeElapsedRef.current = 0;
  }, []);

  const close = useCallback(
    ({ restoreFocus = true } = {}) => {
      clearCloseTimer();
      clearAutoFade();
      const current = stateRef.current;
      const surfaceId = current?.surfaceId;
      setState(null);
      if (restoreFocus && surfaceId) {
        requestAnimationFrame(() => {
          triggerRefs.current.get(surfaceId)?.focus?.();
          current?.anchorEl?.focus?.();
        });
      }
    },
    [clearAutoFade, clearCloseTimer],
  );

  const startAutoFadeTimer = useCallback((surfaceId, remainingMs) => {
    if (autoFadeTimerRef.current) {
      window.clearTimeout(autoFadeTimerRef.current);
    }
    autoFadeStartRef.current = Date.now();
    autoFadeTimerRef.current = window.setTimeout(
      () => {
        autoFadeTimerRef.current = null;
        const latest = stateRef.current;
        if (latest && latest.surfaceId === surfaceId) {
          // Auto-fade closes pinned tooltips too — the upper bound is unconditional.
          setState(null);
        }
        autoFadeElapsedRef.current = 0;
      },
      Math.max(0, remainingMs),
    );
  }, []);

  // Pause the upper-bound timer (cursor entered tooltip body — user is reading).
  const pauseAutoFade = useCallback(() => {
    if (!autoFadeTimerRef.current) return;
    window.clearTimeout(autoFadeTimerRef.current);
    autoFadeTimerRef.current = null;
    if (autoFadeStartRef.current) {
      autoFadeElapsedRef.current += Date.now() - autoFadeStartRef.current;
      autoFadeStartRef.current = 0;
    }
  }, []);

  // Resume the upper-bound timer (cursor left tooltip body).
  const resumeAutoFade = useCallback(
    (surfaceId) => {
      if (!surfaceId) return;
      if (autoFadeTimerRef.current) return; // already running
      const remaining = AUTO_FADE_MS - autoFadeElapsedRef.current;
      startAutoFadeTimer(surfaceId, remaining);
    },
    [startAutoFadeTimer],
  );

  const syncAnchorRect = useCallback((surfaceId) => {
    const current = stateRef.current;
    if (!surfaceId || current?.surfaceId !== surfaceId) return;
    const nextRect = rectFromNode(current.anchorEl || triggerRefs.current.get(surfaceId));
    if (!nextRect) return;
    setState((previous) =>
      previous && previous.surfaceId === surfaceId
        ? { ...previous, anchorRect: nextRect }
        : previous,
    );
  }, []);

  useLayoutEffect(() => {
    if (!state?.surfaceId) return undefined;
    syncAnchorRect(state.surfaceId);
    const handleLayout = () => syncAnchorRect(state.surfaceId);
    window.addEventListener('resize', handleLayout);
    window.addEventListener('scroll', handleLayout, true);
    return () => {
      window.removeEventListener('resize', handleLayout);
      window.removeEventListener('scroll', handleLayout, true);
    };
  }, [state?.surfaceId, syncAnchorRect]);

  useEffect(() => {
    close({ restoreFocus: false });
  }, [location.pathname, close]);

  useEffect(() => {
    if (!state?.pinned) return undefined;
    const handlePointerDown = (event) => {
      const trigger = triggerRefs.current.get(state.surfaceId);
      if (tooltipRef.current?.contains(event.target)) return;
      if (trigger?.contains?.(event.target)) return;
      close();
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [state?.pinned, state?.surfaceId, close]);

  const showTransient = useCallback(
    ({
      surfaceId,
      payload,
      anchorEl,
      testIdPrefix = 'rich-tooltip',
      onDrill = null,
      onAfterOpen = null,
    }) => {
      if (disabled) return;
      if (!payload) return;
      // If a different tooltip is already pinned, hover-show is suppressed.
      // Critically, we must NOT reset/restart the auto-fade timer in that case —
      // doing so would re-key the timer to the hovered surfaceId and let the
      // pinned tooltip live past its 5s upper bound when the timer fires for
      // a surfaceId that no longer matches state.
      const current = stateRef.current;
      if (current?.pinned && current.surfaceId !== surfaceId) return;
      clearCloseTimer();
      const next = {
        surfaceId,
        payload,
        pinned: false,
        anchorEl,
        anchorRect: rectFromNode(anchorEl),
        testIdPrefix,
        onDrill,
      };
      setState(next);
      onAfterOpen?.(next);
      // Reset and start the upper-bound visibility timer on every (re-)open.
      autoFadeElapsedRef.current = 0;
      startAutoFadeTimer(surfaceId, AUTO_FADE_MS);
    },
    [clearCloseTimer, disabled, startAutoFadeTimer],
  );

  const pin = useCallback(
    ({ surfaceId, payload, anchorEl, testIdPrefix = 'rich-tooltip', onDrill = null }) => {
      if (disabled) return;
      if (!payload) return;
      if (payload.interactionMode === RICH_TOOLTIP_INTERACTION_MODES.HOVER_ONLY) return;
      clearCloseTimer();
      setState({
        surfaceId,
        payload,
        pinned: true,
        anchorEl,
        anchorRect: rectFromNode(anchorEl),
        testIdPrefix,
        onDrill,
      });
      // Pinned tooltips also obey the upper bound.
      autoFadeElapsedRef.current = 0;
      startAutoFadeTimer(surfaceId, AUTO_FADE_MS);
    },
    [clearCloseTimer, disabled, startAutoFadeTimer],
  );

  const scheduleClose = useCallback(
    (surfaceId, relatedTarget) => {
      const current = stateRef.current;
      if (!current || current.pinned || current.surfaceId !== surfaceId) return;
      if (relatedTarget instanceof Node && tooltipRef.current?.contains(relatedTarget)) return;
      clearCloseTimer();
      closeTimerRef.current = window.setTimeout(() => {
        const latest = stateRef.current;
        if (latest && !latest.pinned && latest.surfaceId === surfaceId) {
          setState(null);
        }
      }, CLOSE_DELAY_MS);
    },
    [clearCloseTimer],
  );

  const registerTrigger = useCallback((surfaceId) => {
    return (node) => {
      if (node) triggerRefs.current.set(surfaceId, node);
      else triggerRefs.current.delete(surfaceId);
    };
  }, []);

  const getTriggerProps = useCallback(
    ({
      surfaceId,
      payload,
      testIdPrefix = 'rich-tooltip',
      onClick,
      onKeyDown,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      onDrill = null,
      ariaLabel,
    }) => {
      const current = stateRef.current;
      const active = current?.surfaceId === surfaceId;
      const pinned = active && current?.pinned;
      const summaryId = `${testIdPrefix}-summary`;
      return {
        ref: registerTrigger(surfaceId),
        'aria-label': ariaLabel,
        'aria-describedby': active && !pinned ? summaryId : undefined,
        'aria-expanded':
          payload?.interactionMode === RICH_TOOLTIP_INTERACTION_MODES.PINNABLE
            ? pinned
              ? 'true'
              : 'false'
            : undefined,
        'aria-haspopup':
          payload?.interactionMode === RICH_TOOLTIP_INTERACTION_MODES.PINNABLE
            ? 'dialog'
            : undefined,
        onMouseEnter: (event) => {
          showTransient({
            surfaceId,
            payload,
            anchorEl: event.currentTarget,
            testIdPrefix,
            onDrill,
          });
          onMouseEnter?.(event);
        },
        onMouseLeave: (event) => {
          scheduleClose(surfaceId, event.relatedTarget);
          onMouseLeave?.(event);
        },
        onFocus: (event) => {
          showTransient({
            surfaceId,
            payload,
            anchorEl: event.currentTarget,
            testIdPrefix,
            onDrill,
          });
          onFocus?.(event);
        },
        onBlur: (event) => {
          const relatedTarget = event.relatedTarget;
          if (relatedTarget instanceof Node && tooltipRef.current?.contains(relatedTarget)) {
            onBlur?.(event);
            return;
          }
          scheduleClose(surfaceId, relatedTarget);
          onBlur?.(event);
        },
        onClick: (event) => {
          if (payload?.interactionMode === RICH_TOOLTIP_INTERACTION_MODES.PINNABLE) {
            pin({
              surfaceId,
              payload,
              anchorEl: event.currentTarget,
              testIdPrefix,
              onDrill,
            });
          }
          onClick?.(event);
        },
        onKeyDown: (event) => {
          if (
            event.key === 'Tab' &&
            !event.shiftKey &&
            stateRef.current?.pinned &&
            stateRef.current?.surfaceId === surfaceId &&
            linkRef.current
          ) {
            event.preventDefault();
            linkRef.current.focus();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            close();
          }
          onKeyDown?.(event);
        },
      };
    },
    [close, pin, registerTrigger, scheduleClose, showTransient],
  );

  const value = useMemo(
    () => ({
      close,
      getTriggerProps,
      isActive: (surfaceId) => state?.surfaceId === surfaceId,
      isPinned: (surfaceId) => state?.surfaceId === surfaceId && state?.pinned,
    }),
    [close, getTriggerProps, state?.pinned, state?.surfaceId],
  );

  // If the provider becomes disabled while a tooltip is showing, close it
  // immediately so the demo driver's "Turn rich tooltips off" toggle feels snappy.
  useEffect(() => {
    if (disabled && stateRef.current) {
      close({ restoreFocus: false });
    }
  }, [disabled, close]);

  return (
    <RichTooltipContext.Provider value={value}>
      {children}
      <RichTooltipCard
        payload={state?.payload}
        open={Boolean(state?.payload)}
        pinned={Boolean(state?.pinned)}
        anchorRect={state?.anchorRect}
        testIdPrefix={state?.testIdPrefix || 'rich-tooltip'}
        linkRef={linkRef}
        tooltipRef={tooltipRef}
        onMouseEnter={() => {
          clearCloseTimer();
          pauseAutoFade();
        }}
        onMouseLeave={(event) => {
          scheduleClose(state?.surfaceId, event.relatedTarget);
          resumeAutoFade(state?.surfaceId);
        }}
        onRequestClose={close}
        onDrill={state?.onDrill}
      />
    </RichTooltipContext.Provider>
  );
}

export function useRichTooltipController() {
  const context = useContext(RichTooltipContext);
  if (!context) {
    throw new Error('useRichTooltipController must be used within a RichTooltipProvider');
  }
  return context;
}

export function useOptionalRichTooltipController() {
  return useContext(RichTooltipContext);
}
